// Eskom PARCC — process-wide read-only guard for admin impersonation.
//
// When an admin previews the app "as" another role (see src/lib/auth.tsx
// impersonation), the preview must be READ-ONLY: user-data writes are blocked so
// the preview has no side effects and the audit trail is never mis-attributed to
// the admin under a borrowed identity. The AuthProvider toggles this flag; the
// write chokepoints (src/lib/dbWrite user-data mutations + adminApi.authedPost)
// call `assertWritable()`. Best-effort logging (audit / ReportRun) is deliberately
// NOT guarded, so report previews still work while impersonating.
//
// A module-level flag (not React state) is intentional: the write chokepoints are
// plain modules, not components, and there is exactly one app/session per process.

let readOnly = false;

/** Error thrown by {@link assertWritable} when a write is attempted in preview. */
export class ReadOnlyError extends Error {
  constructor() {
    super('You are previewing as another role. Exit preview to make changes.');
    this.name = 'ReadOnlyError';
  }
}

/** Enter/leave read-only mode (called by the AuthProvider on impersonation). */
export function setReadOnly(value: boolean): void {
  readOnly = value;
}

/** Whether writes are currently blocked. */
export function isReadOnly(): boolean {
  return readOnly;
}

/** Throw a {@link ReadOnlyError} when in read-only mode; no-op otherwise. */
export function assertWritable(): void {
  if (readOnly) throw new ReadOnlyError();
}
